import React, { useState, useEffect, forwardRef } from "react";
import { Paper, Typography, Box, IconButton, Tooltip, Popover, FormControlLabel, Checkbox, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Loader } from "../loader/loader";
import { createPortal } from "react-dom";
import { useUser } from "../../../functions/userContext";
import { useTheme } from "@mui/material/styles";
import styles from "./widgetContainer.module.css";

export const WidgetContainer = forwardRef(
  (
    {
      children,
      loaded = false,
      title = "",
      description = "",
      updatedAt = "",
      download = false,
      downloadTooltip = "Download",
      downloadDropdown = false,
      maximize = false,
      insights = false,
      onDownload = () => {},
      onInsights = () => {},
      editMode = false,
      draggable = false,
      loadingMessage = "Loading...",
      maximized = () => {},
      customWidgetActions = [],
    },
    ref
  ) => {
    const [isMaximized, setIsMaximized] = useState(false);
    const [downloadAnchor, setDownloadAnchor] = useState(null);
    const [includeNonFinal, setIncludeNonFinal] = useState(false);
    const { ROLE_CONFIG, checkAccess } = useUser();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    useEffect(() => {
      const onEsc = (e) => {
        if (e.key === "Escape") setIsMaximized(false);
      };
      window.addEventListener("keydown", onEsc);
      return () => window.removeEventListener("keydown", onEsc);
    }, []);

    const handleMaximize = () => {
      setIsMaximized((prev) => !prev);
      maximized(!isMaximized);
    };

    const widgetBg = isDark ? "#414141" : "#e0e0e0";

    const paperStyles = {
      backgroundColor: theme.palette.background.accent,
      color: theme.palette.text.primary,
      position: isMaximized ? "fixed" : "relative",
      boxShadow: isDark ? "0 0 8px rgba(0,0,0,0.3)" : "0 0 4px rgba(0,0,0,0.2)",

      ...(isMaximized && {
        top: 85,
        left: 70,
        width: "calc(100vw - 85px)",
        height: "calc(100vh - 95px)",
        zIndex: 1300,
      }),
    };

    const content = (
      <Paper
        elevation={0}
        className={`${styles.container} ${editMode && !isMaximized ? "drag-handle" : ""}`}
        sx={paperStyles}
      >
        {/* Title */}
        <div
          className={styles.titleBar}
          style={{
            backgroundColor: theme.palette.background.accent,
            cursor: editMode ? "move" : "default",
            borderTop: `2px solid ${isDark ? "#555" : "#eee"}`,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
              {title.toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: isDark ? "#aaa" : "#666" }}>
              {description} {updatedAt && `(Last updated: ${updatedAt})`}
            </Typography>
          </Box>
        </div>

        {/* Controls */}
        {(maximize || download || insights || editMode) && (
          <div
            className={styles.buttonBar}
            style={{
              backgroundColor: theme.palette.background.accent,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {customWidgetActions.map((action, index) => (
                <IconButton
                  key={index}
                  onClick={() => action.onClick()}
                  sx={{ color: theme.palette.text.primary, mr: 0.5, p: 0.5 }}
                >
                  {/* icon is the image name in the assets folder */}
                  <img
                    src={require(`../../../assets/${action.icon}.png`)}
                    title={action.label}
                    style={{ width: "16px", height: "16px" }}
                  />
                </IconButton>
              ))}

              {insights && !editMode && checkAccess([...ROLE_CONFIG.admin]) && (
                <IconButton
                  onClick={onInsights}
                  disableRipple
                  size="small"
                  sx={{
                    color: theme.palette.text.primary,
                    mr: 0.5,
                    p: 0.5,
                    "& svg": {
                      transition: "transform 0.2s, color 0.2s",
                    },
                    "&:hover svg": {
                      transform: "scale(1.2)",
                      color: "#C14D41",
                    },
                  }}
                >
                  <AutoAwesomeIcon fontSize="12" />
                </IconButton>
              )}

              {download && !editMode && (
                <>
                  <Tooltip title={downloadTooltip} placement="top">
                    <IconButton
                      disableRipple
                      size="small"
                      sx={{
                        color: theme.palette.text.primary,
                        mr: 0.5,
                        p: 0.5,
                        "& svg": {
                          transition: "transform 0.2s, color 0.2s",
                        },
                        "&:hover svg": {
                          transform: "scale(1.2)",
                          color: "#C14D41",
                        },
                      }}
                      onClick={downloadDropdown ? (e) => setDownloadAnchor(e.currentTarget) : onDownload}
                    >
                      <DownloadIcon fontSize="12" />
                    </IconButton>
                  </Tooltip>
                  {downloadDropdown && (
                    <Popover
                      open={Boolean(downloadAnchor)}
                      anchorEl={downloadAnchor}
                      onClose={() => { setDownloadAnchor(null); setIncludeNonFinal(false); }}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 1,
                            p: 2,
                            minWidth: 220,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${isDark ? "#555" : "#ddd"}`,
                            boxShadow: isDark
                              ? "0 4px 20px rgba(0, 0, 0, 0.5)"
                              : "0 4px 20px rgba(0, 0, 0, 0.12)",
                          },
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeNonFinal}
                            onChange={(e) => setIncludeNonFinal(e.target.checked)}
                            size="small"
                            sx={{
                              color: isDark ? "#aaa" : "#888",
                              "&.Mui-checked": { color: theme.palette.primary.main },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.primary, userSelect: "none" }}
                          >
                            Include non-final data
                          </Typography>
                        }
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          onDownload({ includeNonFinal });
                          setDownloadAnchor(null);
                          setIncludeNonFinal(false);
                        }}
                        sx={{
                          mt: 1.5,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        Download
                      </Button>
                    </Popover>
                  )}
                </>
              )}

              {maximize && !editMode && (
                <IconButton
                  disableRipple
                  size="small"
                  sx={{
                    color: theme.palette.text.primary,
                    mr: 0.5,
                    p: 0.5,
                    "& svg": {
                      transition: "transform 0.2s, color 0.2s",
                    },
                    "&:hover svg": {
                      transform: "scale(1.2)",
                      color: "#C14D41",
                    },
                  }}
                  onClick={handleMaximize}
                >
                  {isMaximized ? (
                    <FullscreenExitIcon fontSize="12" />
                  ) : (
                    <FullscreenIcon fontSize="12" />
                  )}
                </IconButton>
              )}

              {editMode && (
                <IconButton
                  size="small"
                  disabled
                  sx={{ color: theme.palette.text.primary, cursor: "move" }}
                >
                  <img
                    src="data:image/svg+xml,%3Csvg fill='white' height='18' viewBox='0 0 24 24' width='18' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 9h2v2H3V9zm4 0h2v2H7V9zm4 0h2v2h-2V9zm4 0h2v2h-2V9zm4 0h2v2h-2V9zM3 13h2v2H3v-2zm4 0h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z'/%3E%3C/svg%3E"
                    alt="drag"
                    className={styles.dragIcon}
                  />
                </IconButton>
              )}
            </Box>
          </div>
        )}

        {/* Loader or content */}
        {!loaded ? (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loader message={loadingMessage} />
          </Box>
        ) : (
          children
        )}
      </Paper>
    );

    return isMaximized ? createPortal(content, document.body) : content;
  }
);
